/**
 * RevisionHistory — panel displaying revision history for the active page.
 * Fetches and shows revisions newest-first, allows selecting two for diff,
 * and includes a "Restore" button for each revision.
 */

import React, { useCallback, useEffect, useState } from 'react';
import * as pagesApi from '../../api/pages-api';
import type { PageRevision } from '../../api/pages-api';
import { usePagesContext } from '../../state/pages-context';
import { Button } from '../../components/ui/button';
import { DiffView } from './DiffView';

interface RevisionHistoryProps {
  readonly pageId: string;
}

export function RevisionHistory({ pageId }: RevisionHistoryProps): React.ReactElement {
  const { fetchPage } = usePagesContext();
  const [revisions, setRevisions] = useState<PageRevision[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRevisions, setSelectedRevisions] = useState<string[]>([]);
  const [showDiff, setShowDiff] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);

  // Fetch revisions on mount or pageId change
  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const response = await pagesApi.listRevisions(pageId);
        if (!cancelled) {
          setRevisions(response.data ?? (response as any).revisions ?? []);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load revisions');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [pageId]);

  const handleRevisionToggle = useCallback((timestamp: string) => {
    setSelectedRevisions((prev) => {
      if (prev.includes(timestamp)) {
        return prev.filter((t) => t !== timestamp);
      }
      if (prev.length >= 2) {
        // Replace the oldest selection
        return [prev[1], timestamp];
      }
      return [...prev, timestamp];
    });
    setShowDiff(false);
  }, []);

  const handleCompare = useCallback(() => {
    if (selectedRevisions.length === 2) {
      setShowDiff(true);
    }
  }, [selectedRevisions]);

  const handleRestore = useCallback(async (timestamp: string) => {
    setRestoring(timestamp);
    try {
      await pagesApi.restoreRevision(pageId, timestamp);
      // Refresh the page to get the restored content
      await fetchPage(pageId);
      // Refresh revision list
      const response = await pagesApi.listRevisions(pageId);
      setRevisions(response.data ?? (response as any).revisions ?? []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to restore revision');
    } finally {
      setRestoring(null);
    }
  }, [pageId, fetchPage]);

  const formatTimestamp = (ts: string): string => {
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return ts;
    }
  };

  if (loading) {
    return (
      <div className="p-4" role="status" aria-live="polite">
        <p className="text-sm text-muted-foreground">Loading revisions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4" role="alert">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <section aria-label="Revision history" className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Revision History</h3>
        {selectedRevisions.length === 2 && (
          <Button size="sm" variant="outline" onClick={handleCompare}>
            Compare
          </Button>
        )}
      </div>

      {revisions.length === 0 && (
        <p className="text-xs text-muted-foreground">No revisions found.</p>
      )}

      <ul className="space-y-1" role="list" aria-label="Revisions">
        {revisions.map((revision) => {
          const isSelected = selectedRevisions.includes(revision.timestamp);
          return (
            <li
              key={revision.timestamp}
              className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs ${
                isSelected ? 'bg-accent' : 'hover:bg-accent/50'
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleRevisionToggle(revision.timestamp)}
                className="h-3 w-3 rounded border"
                aria-label={`Select revision from ${formatTimestamp(revision.timestamp)}`}
              />
              <div className="flex-1 min-w-0">
                <p className="truncate font-medium">{formatTimestamp(revision.timestamp)}</p>
                <p className="text-muted-foreground truncate">
                  {revision.authorId}
                  {revision.changeNote ? ` — ${revision.changeNote}` : ''}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="text-xs h-6 px-2"
                onClick={() => handleRestore(revision.timestamp)}
                disabled={restoring === revision.timestamp}
              >
                {restoring === revision.timestamp ? '...' : 'Restore'}
              </Button>
            </li>
          );
        })}
      </ul>

      {/* Diff view when two revisions are compared */}
      {showDiff && selectedRevisions.length === 2 && (
        <DiffView
          pageId={pageId}
          fromTimestamp={selectedRevisions[0]}
          toTimestamp={selectedRevisions[1]}
        />
      )}
    </section>
  );
}
